import React, { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ExclamationTriangleIcon } from '../../../assets';
import { theme } from '../../../theme';
import { BodySize } from '../../typography';
import { floatLabel } from '../helpers';
import { Label } from '../label';
import {
  FieldStyleProps,
  FieldStyles,
  InputStyleProps,
  InputStyles,
  InputWrapperStyleProps,
  InputWrapperStyles,
} from '../styles';
import { ErrorBox } from '../errorBox';

export type TextAreaProps = {
  disabled?: boolean;
  errorMessage?: string;
  isCompact?: boolean;
  label?: string;
  hasWrapperMarginBottom?: boolean;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  rows?: number;
  size?: BodySize;
  value?: string;
};

const Wrapper = styled.div<FieldStyleProps>`
  ${FieldStyles}
`;

const InputWrapper = styled.div<InputWrapperStyleProps>`
  ${InputWrapperStyles}
  align-items: baseline;
  position: relative;
`;

const Input = styled.textarea<InputStyleProps & { hasError: boolean }>`
  ${InputStyles}
  ${({ hasError }) => hasError && `padding-right: ${theme.spacings[8].rem};`}
  resize: vertical;
`;

const ErrorIcon = styled(ExclamationTriangleIcon)`
  fill: ${theme.colors.system.systemRed};
  margin: 0 ${theme.spacings[3].rem};
  position: absolute;
  right: 0;
  top: ${theme.spacings[3].rem};
  width: 24px;
`;

export const TextArea: React.FC<TextAreaProps> = ({
  disabled = false,
  errorMessage,
  isCompact = false,
  label,
  hasWrapperMarginBottom = true,
  onChange,
  placeholder,
  readOnly = false,
  required = false,
  rows = 4,
  size = 'md',
  value,
  ...props
}) => {
  const [isFocussed, setIsFocussed] = useState(false);
  const [input, setInput] = useState(value);
  const isLabelFloating = floatLabel(isCompact, isFocussed, !input);

  useEffect(() => {
    if (disabled) setIsFocussed(false);
  }, [disabled]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value);
    onChange(event);
  };

  return (
    <Wrapper haveMarginBottom={hasWrapperMarginBottom} isCompact={isCompact}>
      {label && (
        <Label
          float={isLabelFloating}
          isCompact={isCompact}
          required={required}
          value={label}
        />
      )}
      <InputWrapper
        disabled={disabled}
        hasError={!!errorMessage}
        isFocussed={isFocussed}
        readOnly={readOnly}
      >
        <Input
          disabled={disabled}
          hasError={!!errorMessage}
          isCompact={isCompact}
          onChange={handleChange}
          onTouchStart={() => setIsFocussed(true)}
          onFocus={() => setIsFocussed(true)}
          onBlur={() => setIsFocussed(false)}
          placeholder={placeholder}
          readOnly={readOnly}
          rows={rows}
          showPlaceholder={!isCompact || isLabelFloating}
          textSize={size}
          value={input}
          {...props}
        />
        {errorMessage && <ErrorIcon />}
      </InputWrapper>
      {errorMessage && <ErrorBox size="sm">{errorMessage}</ErrorBox>}
    </Wrapper>
  );
};
